import { createTheme } from '@mui/material';
import { shouldSkipGeneratingVar } from '@mui/material/styles';
import { createSpacing } from '@mui/system';
import { MuiOutlinedInputStyle } from './input';
import { CRM_BAUSTEIN_MASSE } from './bausteinMasse';
import { dunklePalette, hellePalette, tokenVar } from './farben';

// Korrekturrunde 1 (26.10.2026): eigene Typo-Rollen erben die Schrift NICHT
// aus `typography.fontFamily` — MUI setzt sie nur fuer die eingebauten
// Varianten. Ohne diese Zeile fiel jede Rolle auf die Browser-Schrift (Arial)
// zurueck (gemessen Sidebar 26.10.: Arial statt Montserrat).
const HAUSSCHRIFT = '"Montserrat", sans-serif';

/**
 * Mit CSS-Variablen rechnet MUI Abstaende als `calc(n * var(--mui-spacing))`
 * (Dark Mode, Decision #248). Hier zurueck auf feste px je Theme: `spacing(1)`
 * ist wieder `4px` bzw. `3px`, und ein verschachteltes Theme (Sidebar im
 * Kompakt-Modus) behaelt seine eigene Dichte. `generateSpacing` ruft der
 * Provider bei jedem Schema-Wechsel.
 */
const mitFesterDichte = <T extends { spacing: unknown; generateSpacing?: unknown }>(t: T, stufe: number): T => {
  const spacing = createSpacing(stufe);
  return Object.assign(t, { spacing, generateSpacing: () => spacing });
};

/**
 * Theme als Fabrik (Kompakt-Modus, Decision #247): `kleinerBildschirm` schaltet
 * die Dichte um — Abstaende 3 statt 4 px je Stufe, Tabellenzeilen 36 statt
 * 44 px, Kartenpolster 12 statt 16 px. Schrift und Farben bleiben gleich.
 * Die Schwelle steht in `kleinerBildschirm.ts`.
 */
export const erstelleTheme = (kleinerBildschirm = false) => mitFesterDichte(createTheme({
  spacing: kleinerBildschirm ? 3 : 4,
  // Masse der gemeinsamen Bausteine (Alt-Theme der Strecke ueberschreibt sie).
  bausteinMasse: CRM_BAUSTEIN_MASSE,
  /**
   * DESIGN-TEST 25.10.2026 (Branch session/design-test, Wegwerf): ruhiger Look.
   * Vier Groessen 24/16/14/12, Gewichte 400/500/600 (kein 700), Headlines
   * leicht negatives Letter-Spacing. Schrift bleibt Montserrat (CI).
   */
  shape: {
    borderRadius: 8,
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 150,
      short: 150,
      standard: 150,
      complex: 150,
      enteringScreen: 150,
      leavingScreen: 150,
    },
  },
  typography: {
    fontFamily: HAUSSCHRIFT,
    fontWeightBold: 600,
    h1: {
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.02em',
      fontOpticalSizing: 'auto',
    },
    h2: {
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.02em',
      fontOpticalSizing: 'auto',
    },
    h3: {
      fontWeight: 600,
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.02em',
      fontOpticalSizing: 'auto',
    },
    h4: {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '-0.01em',
    },
    body1: {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      fontOpticalSizing: 'auto',
      whiteSpace: 'pre-line',
    },
    body2: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '18px',
      fontOpticalSizing: 'auto',
      whiteSpace: 'pre-line',
    },
    button: {
      fontSize: '14px',
      fontWeight: 500,
      textTransform: 'none',
      fontOpticalSizing: 'auto',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '-0.01em',
      fontOpticalSizing: 'auto',
    },
    subtitle2: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '-0.01em',
      color: tokenVar('text.secondary'),
      fontOpticalSizing: 'auto',
    },
    overline: {
      fontSize: '12px',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: 0,
      color: tokenVar('text.muted'),
    },
    caption: {
      fontSize: '12px',
      fontWeight: 500,
    },
    /**
     * DESIGN-TEST STUFE 2 (25.10.2026): benannte Text-Rollen. Der Screen-Code
     * nennt nur noch die Rolle (`variant="label"` bzw. `typography: 'label'`
     * im sx) — keine eigene Groesse, kein Gewicht, keine Farbe. Typen in
     * `style.d.ts`, Element-Zuordnung unten bei `MuiTypography`.
     */
    titelGross: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
      letterSpacing: '-0.02em',
      color: tokenVar('text.secondary'),
    },
    // Korrekturrunde 3 (Alex 26.10.2026): Bereichs-Titel (Seitenkopf) und
    // Kundenname im Fall-Kopf 20px. Eigene Rolle, weil titelGross an rund
    // 30 weiteren Stellen (Kennzahlen, Rechner, Wizard) 24px behalten soll.
    bereichstitel: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '28px',
      letterSpacing: '-0.02em',
      color: tokenVar('text.secondary'),
    },
    seitentitel: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '-0.01em',
      color: tokenVar('text.secondary'),
    },
    kartentitel: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      color: tokenVar('text.secondary'),
    },
    // Korrekturrunde 2: 450 statt 400 — Montserrat 400 wirkt bei 14px duenn und
    // grau (variable Schrift, 100–900 geladen).
    wert: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '14px',
      fontWeight: 450,
      lineHeight: '20px',
      color: tokenVar('text.secondary'),
    },
    wertBetont: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      color: tokenVar('text.secondary'),
    },
    meta: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '14px',
      fontWeight: 450,
      lineHeight: '20px',
      color: tokenVar('text.primary'),
    },
    label: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      color: tokenVar('text.primary'),
    },
    klein: {
      fontFamily: HAUSSCHRIFT,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '16px',
      color: tokenVar('text.muted'),
    },
  },
  /**
   * Dark Mode (Decision #248, 28.10.2026): CSS-Variablen und zwei Farbschemata.
   * Paletten in `farben.ts` — hell Zeichen fuer Zeichen wie vorher. Das
   * Schema haengt als `data-mui-color-scheme` am <html> (Ladeskript in
   * `public/index.html`, Umschalter im Konto-Menue, Hell-Sperre in
   * `farbschema.tsx`). Overrides und Typo-Rollen nennen deshalb keine Werte,
   * sondern `tokenVar(...)`.
   */
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
    // Kein automatisches `color-scheme: light` — vorher stand dort `normal`, und
    // Chrome glaettet damit Kanten minimal anders (Suchfeld-Ecke, Screenshot-
    // Vergleich 28.10.). `dark` setzt CssBaseline unten nur fuers dunkle Schema.
    disableCssColorScheme: true,
    // Keine Abstands-Variable: sie haenge am <html> und gaelte fuer JEDEN
    // Provider — die Sidebar steht im Kompakt-Modus aber bewusst im normalen
    // Theme (Decision #247). Abstaende bleiben feste px wie vorher.
    shouldSkipGeneratingVar: (keys: string[]) => shouldSkipGeneratingVar(keys) || keys[0] === 'spacing',
  },
  colorSchemes: {
    light: { palette: hellePalette },
    dark: { palette: dunklePalette },
  },
  components: {
    // ─── DESIGN-TEST 25.10.2026: Flaechen, Schatten, Ecken ─────────────────
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          titelGross: 'div',
          bereichstitel: 'div',
          seitentitel: 'div',
          kartentitel: 'div',
          // Text-Rollen wie body1/body2 als Absatz (Tests und Semantik
          // lesen Feldwerte als <p>), Titel-Rollen als Block.
          wert: 'p',
          wertBetont: 'p',
          meta: 'p',
          label: 'p',
          klein: 'p',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Native Bedienelemente (Scrollbalken, Datumsfelder) im Dunkeln dunkel.
        ':root[data-mui-color-scheme="dark"]': { colorScheme: 'dark' },
        body: {
          backgroundColor: tokenVar('background.default'),
          color: tokenVar('text.secondary'),
        },
      },
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { boxShadow: 'none', backgroundImage: 'none' },
        rounded: { borderRadius: 8 },
        outlined: { borderColor: tokenVar('divider') },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { border: `1px solid ${tokenVar('divider')}`, borderRadius: 8, boxShadow: 'none' },
      },
    },
    // Kompakt: MUI-Standard 16/24 px -> 12 px. Normal bleibt der MUI-Standard.
    MuiCardContent: {
      styleOverrides: {
        root: kleinerBildschirm ? { padding: 12, '&:last-child': { paddingBottom: 12 } } : {},
      },
    },
    // Ausnahme Schatten: Dialoge und schwebende Flaechen behalten einen leichten.
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: `1px solid ${tokenVar('divider')}`,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${tokenVar('divider')}`,
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: {
          backgroundColor: '#212A48',
          color: '#FFFFFF',
          boxShadow: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokenVar('background.paper'),
          borderColor: tokenVar('divider'),
          boxShadow: 'none',
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: tokenVar('divider') } },
    },
    // ─── Buttons: contained = Primaer-Aktion, outlined = Hairline, text = Abbrechen
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          minHeight: 36,
          boxShadow: 'none',
          fontWeight: 500,
          fontSize: 14,
          '&:hover': { boxShadow: 'none' },
        },
        // Stufe 2b: Sekundaer-Aktion wieder mit Teal-Rand wie Original.
        outlined: {
          border: `1px solid ${tokenVar('primary.main')}`,
          color: tokenVar('primary.main'),
          '&:hover': { border: `1px solid ${tokenVar('primary.main')}`, backgroundColor: tokenVar('primary.hover') },
        },
        text: {
          color: tokenVar('text.primary'),
          '&:hover': { backgroundColor: tokenVar('background.hover') },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 8, '&:hover': { backgroundColor: tokenVar('background.hover') } },
      },
    },
    // ─── Tabellen: 44px, Hairline, ruhiger Kopf, kein Zebra ────────────────
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: kleinerBildschirm ? 36 : 44,
          '&:nth-of-type(odd), &:nth-of-type(even)': { backgroundColor: 'transparent' },
          '&.MuiTableRow-hover:hover': { backgroundColor: tokenVar('background.card') },
        },
        head: { height: kleinerBildschirm ? 36 : 44 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${tokenVar('divider')}`,
          paddingTop: kleinerBildschirm ? 6 : 10,
          paddingBottom: kleinerBildschirm ? 6 : 10,
          fontSize: 14,
          // Kompakt: erste Spalte steht beim seitlichen Scrollen still — gilt fuer
          // jede Tabelle im CRM (DataTable und rohe MUI-Tabellen). Die Strecke hat
          // ihr eigenes Theme und bleibt unberuehrt.
          ...(kleinerBildschirm && {
            '&:first-of-type': { position: 'sticky', left: 0, zIndex: 1, backgroundColor: tokenVar('background.paper') },
          }),
        },
        head: {
          fontSize: 12,
          fontWeight: 500,
          color: tokenVar('text.primary'),
          textTransform: 'none',
          letterSpacing: 0,
          backgroundColor: tokenVar('background.paper'),
          ...(kleinerBildschirm && { '&:first-of-type': { zIndex: 3 } }),
        },
      },
    },
    // ─── Chips: flach, heller Grund, dunkler Text ──────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: 12,
          boxShadow: 'none',
        },
        filled: {
          backgroundColor: tokenVar('background.subtle'),
          color: tokenVar('text.secondary'),
        },
        outlined: {
          borderColor: tokenVar('divider'),
          color: tokenVar('text.secondary'),
        },
        colorPrimary: {
          '&.MuiChip-filled': { backgroundColor: tokenVar('primary.light'), color: tokenVar('primary.main') },
        },
        colorSecondary: {
          '&.MuiChip-filled': { backgroundColor: tokenVar('status.gold.flaeche'), color: tokenVar('status.gold.text') },
        },
        colorSuccess: {
          '&.MuiChip-filled': { backgroundColor: tokenVar('success.light'), color: tokenVar('success.contrastText') },
        },
        colorError: {
          '&.MuiChip-filled': { backgroundColor: tokenVar('error.light'), color: tokenVar('error.contrastText') },
        },
        colorWarning: {
          '&.MuiChip-filled': { backgroundColor: tokenVar('warning.flaeche'), color: tokenVar('warning.text') },
        },
        colorInfo: {
          '&.MuiChip-filled': { backgroundColor: tokenVar('info.flaeche'), color: tokenVar('info.text') },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokenVar('divider')}`, minHeight: 40 },
        indicator: { height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 500, fontSize: 14, minHeight: 40 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: tokenVar('background.tooltip'), fontSize: 12, fontWeight: 500, borderRadius: 8 },
      },
    },
    MuiOutlinedInput: MuiOutlinedInputStyle,
    // N3 Fix: MuiSelect-Display-Text bei size="small" wurde durch die globale
    // MuiOutlinedInput.height=44px vertikal abgeschnitten. Ellipsis-Overflow
    // verhindert weiches Verschwinden längerer Optionen.
    MuiSelect: {
      styleOverrides: {
        select: {
          whiteSpace: 'nowrap' as const,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
    },
    /**
     * 🔥 SCHWEBENDE LABELS WURDEN AM OBEREN DIALOGRAND ABGESCHNITTEN
     * (Screenshot-Befund Alex 08.09.2026 an „Aufgabe erstellen" aus dem Fall —
     * das Label „Kunde" war oben angeschnitten).
     *
     * Der Schnitt entsteht aus DREI MUI-Vorgaben, die einzeln richtig sind:
     *
     *   1. `DialogContent` traegt `overflowY: 'auto'`
     *      (@mui/material/DialogContent/DialogContent.js:38) — es CLIPPT also.
     *   2. Steht ein `DialogTitle` unmittelbar davor, setzt MUI
     *      `.MuiDialogTitle-root + & { paddingTop: 0 }` (ebd. Z. 53-56).
     *      Der Innenabstand nach oben ist damit WEG.
     *   3. Ein geschrumpftes Label bei `size="small"` sitzt auf
     *      `translate(14px, -9px)` (@mui/material/InputLabel/InputLabel.js:169)
     *      — es ragt also 9px UEBER die Feldkante hinaus.
     *
     * Ergebnis: das erste Feld eines Dialogs verliert die oberen ~9px seines
     * Labels. Die betroffenen Dialoge halfen sich bisher mit einem eigenen
     * `pt={1}` am Stack (8px) — knapp zu wenig, und in JEDEM Dialog neu.
     *
     * 🔥 DESHALB HIER UND NICHT PRO DIALOG: der Befund traf nicht „Aufgabe
     * erstellen", sondern das MUSTER. 14 Dialoge im Baum haben dieselbe
     * Struktur (DialogTitle + DialogContent + Feld mit Label als erstes
     * Element). Ein Fix pro Dialog waere 14-mal dieselbe Zahl an 14 Orten —
     * und der 15. Dialog haette den Fehler wieder.
     *
     * 12px statt 9px: die 9px sind die reine Verschiebung, das Label braucht
     * daneben noch seine halbe Zeilenhoehe Luft. `dividers`-Dialoge sind
     * ausgenommen — dort setzt MUI den Abstand selbst und die Trennlinie
     * uebernimmt die optische Trennung.
     */
    // Design-Rollout 25.10.2026: Titel, Inhalt und Aktionen roher MUI-Dialoge
    // auf die Masse der Huelle `Modal` (24px Rand, Titel 16/600).
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 16,
          fontWeight: 600,
          lineHeight: '24px',
          letterSpacing: '-0.01em',
          color: tokenVar('text.secondary'),
          padding: '24px 24px 8px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
          gap: 8,
          '& > :not(style) ~ :not(style)': { marginLeft: 0 },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '.MuiDialogTitle-root + &:not(.MuiDialogContent-dividers)': {
            paddingTop: 12,
          },
        },
      },
    },
  },
}), kleinerBildschirm ? 3 : 4);

const theme = erstelleTheme(false);

/** Kompakte Dichte unter `KLEINER_BILDSCHIRM_BREITE` (Decision #247). */
export const kompaktTheme = erstelleTheme(true);

export default theme;
